import { useMemo } from 'react';
import type { KanbanVisit, VisitKanbanStatus } from '@pages/authenticated/agency-pages/visits/useVisitsKanban';
import { parseBrtDateTime } from '@pages/authenticated/agency-pages/visits/visit-time-conflict';
import { notifications } from '@/shared/ui/components/basic/Notifications';
import { VisitsService } from '@services/requests/housien-services/VisitsService';
import { DEFAULT_VISIT_PERIOD, type VisitFilters } from '../components/VisitFilterPanel';
import type { VisitStatus } from '../constants/visit-colors';

/**
 * Tests whether a visit matches a lowercased search query across property,
 * client, and broker fields.
 *
 * @param v - The visit to test.
 * @param q - The lowercased search query.
 * @returns True when any searchable field contains the query.
 */
function matchesSearch(v: KanbanVisit, q: string): boolean {
  return (
    v.property.toLowerCase().includes(q) ||
    v.client.toLowerCase().includes(q) ||
    v.broker.toLowerCase().includes(q)
  );
}

/**
 * Filters visits by search query, selected statuses, and selected brokers.
 *
 * @param visits - The full list of visits.
 * @param search - The raw search query.
 * @param filters - The active status and broker filters.
 * @returns The filtered list of visits.
 */
function applyVisitFilters(
  visits: KanbanVisit[],
  search: string,
  filters: VisitFilters,
): KanbanVisit[] {
  let result = visits;
  if (search) {
    const q = search.toLowerCase();
    result = result.filter((v) => matchesSearch(v, q));
  }
  if (filters.statuses?.length) {
    result = result.filter((v) => filters.statuses!.includes(v.status));
  }
  if (filters.brokers?.length) {
    result = result.filter((v) => filters.brokers!.includes(v.brokerContact));
  }
  return result;
}

/**
 * Memoizes the filtered visits for the given search and filter inputs.
 *
 * @param visits - The full list of visits.
 * @param search - The raw search query.
 * @param filters - The active status and broker filters.
 * @returns The memoized filtered list of visits.
 */
export function useVisitFiltering(
  visits: KanbanVisit[],
  search: string,
  filters: VisitFilters,
) {
  return useMemo(
    () => applyVisitFilters(visits, search, filters),
    [visits, search, filters],
  );
}

/**
 * Determines whether a visit scheduled in the future may be marked completed.
 * A visit with no scheduled datetime is always completable.
 *
 * @param visit - The visit to check.
 * @returns True when the visit's scheduled time is not in the future.
 */
export function canCompleteFutureVisit(visit: KanbanVisit | undefined): boolean {
  if (!visit?.raw?.visit_datetime) return true;
  const { date, time } = parseBrtDateTime(visit.raw.visit_datetime);
  const visitDate = new Date(`${date}T${time}:00`);
  return visitDate <= new Date();
}

interface StatusChangeHandlerDeps {
  visits: KanbanVisit[];
  updateStatus: (id: string, status: VisitKanbanStatus, extra?: any) => Promise<void>;
  setFeedbackVisitId: (id: string | null) => void;
  setFeedbackRating: (r: number) => void;
  setFeedbackObs: (o: string) => void;
  setFeedbackDialogOpen: (o: boolean) => void;
  setCancelVisitId: (id: string | null) => void;
  setCancelReason: (r: string) => void;
  setCancelDialogOpen: (o: boolean) => void;
  t: (key: string) => string;
}

/**
 * Creates a status-change handler that opens the feedback dialog for
 * completion, the cancel dialog for cancellation, or updates status directly.
 *
 * @param deps - The visits, updater, and dialog-state setters the handler needs.
 * @returns An async handler receiving a visit id and target status.
 */
export function createStatusChangeHandler(deps: StatusChangeHandlerDeps) {
  return async (id: string, status: VisitKanbanStatus) => {
    if (status === 'done') {
      const visit = deps.visits.find((v) => v.id === id);
      if (!canCompleteFutureVisit(visit)) {
        notifications.show({ color: 'red', message: deps.t('visits.feedback.notYet') });
        return;
      }
      deps.setFeedbackVisitId(id);
      deps.setFeedbackRating(0);
      deps.setFeedbackObs('');
      deps.setFeedbackDialogOpen(true);
      return;
    }
    if (status === 'cancelled') {
      deps.setCancelVisitId(id);
      deps.setCancelReason('');
      deps.setCancelDialogOpen(true);
      return;
    }
    await deps.updateStatus(id, status);
  };
}

/**
 * Creates a delete handler that opens a confirmation popup and deletes the
 * visit on confirmation, then clears selection and refetches.
 *
 * @param agencyId - The agency owning the visit.
 * @param setSelectedId - Setter to clear the selected visit id.
 * @param refetch - Callback to reload the visits after deletion.
 * @param updatePopupProps - Setter for the confirmation popup props.
 * @param t - The translation function scoped to the visits namespace.
 * @returns A handler receiving the visit to delete.
 */
export function createDeleteVisitHandler(
  agencyId: string,
  setSelectedId: (id: string | null) => void,
  refetch: () => void,
  updatePopupProps: (props: any) => void,
  t: (key: string) => string,
) {
  return (visit: KanbanVisit) => {
    updatePopupProps({
      visible: true,
      title: t('visits.delete.title'),
      label: t('visits.delete.message'),
      type: 'question',
      onRight: async () => {
        try {
          await VisitsService.deleteVisit(agencyId, visit.id);
          setSelectedId(null);
          refetch();
        } catch { /* global error handler */ }
      },
    });
  };
}

/**
 * Removes the status matching the given label from the active status filters.
 *
 * @param statusLabels - Map of status keys to their localized labels.
 * @param filters - The current visit filters.
 * @param setFilters - Setter to apply the updated filters.
 * @param label - The chip label to remove.
 * @returns True when a matching status filter was removed.
 */
function removeStatusFilter(
  statusLabels: Record<string, string>,
  filters: VisitFilters,
  setFilters: (f: VisitFilters) => void,
  label: string,
): boolean {
  const entry = Object.entries(statusLabels).find(([, v]) => v === label);
  if (!entry || !filters.statuses) return false;
  setFilters({ ...filters, statuses: filters.statuses.filter((s) => s !== entry[0]) });
  return true;
}

/**
 * Removes the broker matching the given display-name label from the active
 * broker filters.
 *
 * @param filters - The current visit filters.
 * @param setFilters - Setter to apply the updated filters.
 * @param agency - The agency whose members resolve the display name.
 * @param label - The broker display-name chip label to remove.
 */
function removeBrokerFilter(
  filters: VisitFilters,
  setFilters: (f: VisitFilters) => void,
  agency: any,
  label: string,
): void {
  if (!filters.brokers) return;
  const member = agency?.members?.find((m: any) => {
    const displayName =
      m.name || [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email;
    return displayName === label;
  });
  if (!member?.email) return;
  setFilters({ ...filters, brokers: filters.brokers.filter((b) => b !== member.email) });
}

/**
 * Creates a chip-removal handler that clears either a status or broker filter
 * matching the removed chip's label.
 *
 * @param statusLabels - Map of status keys to their localized labels.
 * @param filters - The current visit filters.
 * @param setFilters - Setter to apply the updated filters.
 * @param agency - The agency whose members resolve broker display names.
 * @returns A handler receiving the chip label to remove.
 */
export function createRemoveFilterHandler(
  statusLabels: Record<string, string>,
  filters: VisitFilters,
  setFilters: (f: VisitFilters) => void,
  agency: any,
  periodOptions: Array<{ key: string; label: string }> = [],
) {
  return (label: string) => {
    if (removePeriodFilter(filters, setFilters, periodOptions, label)) return;
    const handled = removeStatusFilter(statusLabels, filters, setFilters, label);
    if (!handled) removeBrokerFilter(filters, setFilters, agency, label);
  };
}

/**
 * Sends the period back to the default when its chip is the one removed.
 *
 * @param filters - The current visit filters.
 * @param setFilters - Setter to apply the updated filters.
 * @param periodOptions - The period keys with their localized labels.
 * @param label - The chip label being removed.
 * @returns True when the label was the period chip.
 */
function removePeriodFilter(
  filters: VisitFilters,
  setFilters: (f: VisitFilters) => void,
  periodOptions: Array<{ key: string; label: string }>,
  label: string,
): boolean {
  const applied = periodOptions.find((o) => o.key === filters.period);
  if (!applied || applied.label !== label) return false;
  setFilters({ ...filters, period: DEFAULT_VISIT_PERIOD });
  return true;
}
